import { inject, NgModule } from '@angular/core';
import { provideApollo } from 'apollo-angular';
import { ApolloClient, ApolloLink, gql, InMemoryCache } from '@apollo/client';
import { SetContextLink } from '@apollo/client/link/context';
import { HttpLink } from 'apollo-angular/http';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { getMainDefinition } from '@apollo/client/utilities';
import { randomUuid } from '@utils/random-uuid.util';
import extractFiles from 'extract-files/extractFiles.mjs';
import isExtractableFile from 'extract-files/isExtractableFile.mjs';

import { createFragmentRegistry } from '@apollo/client/cache';
import { Auth0ConfigService } from '../shared/services/auth0-config.service';
import { AuthService } from '@auth0/auth0-angular';
import { firstValueFrom } from 'rxjs';
import { GraphqlWsLifecycleService } from './graphql-ws-lifecycle.service';
import {
  GRAPHQL_WS_RETRY,
  graphqlWsRetryWait,
  shouldRetryGraphqlWsTransport,
} from './graphql-ws-retry.util';

type ApolloHttpBody = {
  query?: string;
  variables?: Record<string, unknown>;
  operationName?: string;
  extensions?: Record<string, unknown>;
};

const defaultOptions = {
  watchQuery: {
    fetchPolicy: 'no-cache',
    errorPolicy: 'ignore',
  },
  query: {
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  },
} as ApolloClient.DefaultOptions;

export function createApollo(
  httpLink: HttpLink,
  authConfigService: Auth0ConfigService,
  authService: AuthService,
  wsLifecycle: GraphqlWsLifecycleService
): ApolloClient.Options {
  const config = authConfigService.getConfig();
  const uri = config.gqlUrl;
  const wsUri = config.wsUrl;

  const http = httpLink.create({
    uri,
    extractFiles: (body) =>
      extractFiles(body, isExtractableFile) as {
        clone: ApolloHttpBody;
        files: Map<any, any>;
      },
  });

  const ws = new GraphQLWsLink(
    createClient({
      url: wsUri,
      connectionParams: async () => {
        try {
          const token = await firstValueFrom(
            authService.getAccessTokenSilently()
          );
          return {
            Authorization: `Bearer ${token}`,
            'x-correlation-id': randomUuid(),
          };
        } catch (error) {
          console.error(
            'Failed to get access token for WebSocket connection:',
            error
          );
          return { 'x-correlation-id': randomUuid() };
        }
      },
      on: {
        opened: () => wsLifecycle.notifyOpened(),
        error: (error: unknown) =>
          console.error('[GraphQL WS] Connection error', error),
        closed: (event: unknown) => {
          if (event instanceof CloseEvent) {
            console.warn('[GraphQL WS] Connection closed', {
              code: event.code,
              reason: event.reason,
              wasClean: event.wasClean,
            });
            return;
          }
          console.warn('[GraphQL WS] Connection closed', event);
        },
      },
      keepAlive: GRAPHQL_WS_RETRY.keepAliveMs,
      retryAttempts: GRAPHQL_WS_RETRY.maxAttempts,
      retryWait: graphqlWsRetryWait,
      shouldRetry: shouldRetryGraphqlWsTransport,
    })
  );

  const splitLink = ApolloLink.split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        definition.kind === 'OperationDefinition' &&
        definition.operation === 'subscription'
      );
    },
    ws,
    http
  );

  const authLink = new SetContextLink(async (prevContext) => {
    const headers = prevContext.headers ?? {};
    try {
      const token = await firstValueFrom(authService.getAccessTokenSilently());
      return {
        headers: {
          ...headers,
          Authorization: `Bearer ${token}`,
          'x-correlation-id': randomUuid(),
        },
      };
    } catch (error) {
      console.error('Failed to get access token for GraphQL operation:', error);
      return {
        headers: { ...headers, 'x-correlation-id': randomUuid() },
      };
    }
  });

  return {
    link: ApolloLink.from([authLink, splitLink]),
    cache: new InMemoryCache({
      fragments: createFragmentRegistry(gql`
        fragment outgoingWires on Loan {
          outgoingWires {
            id
          }
        }
      `),
    }),
    defaultOptions,
  };
}

@NgModule({
  providers: [
    provideApollo(() =>
      createApollo(
        inject(HttpLink),
        inject(Auth0ConfigService),
        inject(AuthService),
        inject(GraphqlWsLifecycleService)
      )
    ),
  ],
})
export class GraphQLModule {}
